import Vue from "vue";
import VueRouter from "vue-router";

import i18n from "../plugins/i18n.js";
import {
  authRole,
  authUser,
  isAuthenticatedRole,
} from "@/app/user-module/helpers/authHelper";
import { getSavedLanguage } from "@/app/core-module/util/languageHelper";
import { readFromLocalStorage } from "@/app/core-module/util/localStorageHandler";

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    redirect: `/${i18n.locale}/`,
  },
  {
    path: "/:lang",
    redirect: `/${i18n.locale}/home`,
  },
  {
    path: "/:lang",
    component: {
      render: h => h("router-view"),
    },
    children: [
      {
        path: "home",
        name: "Home",
        component: () =>
          /* webpackChunkName: "Home" */ import(
            "../app/core-module/pages/HomePage"
          ),
        meta: { requiresLogin: true },
      },

      {
        path: "sign-in",
        name: "SignIn",
        meta: { redirectAuthUser: true },
        component: () =>
          import(
            /* webpackChunkName: "SignIn" */ "../app/user-module/pages/SignInPage"
          ),
      },
      {
        path: "sign-up/:email/:token",
        name: "SignUp",
        meta: { redirectAuthUser: true },
        component: () =>
          import(
            /* webpackChunkName: "SignUp" */ "../app/user-module/pages/SignUpPage"
          ),
      },
      {
        path: "detail-sign-up",
        name: "DetailSignUp",
        meta: { requiresLogin: true },
        component: () =>
          import(
            /* webpackChunkName: "SignUp" */ "../app/user-module/pages/SignUpDetailsPage"
          ),
      },
      {
        path: "forgot-password",
        name: "ForgotPassword",
        component: () =>
          import(
            /* webpackChunkName: "ForgotPassword" */ "../app/user-module/pages/ForgotPasswordPage"
          ),
      },
      {
        path: "profile",
        name: "Profile",
        meta: { requiresLogin: true },
        component: () =>
          import(
            /* webpackChunkName: "Profile" */ "../app/user-module/pages/ProfilePage"
          ),
      },
      {
        path: "public-profile/:id",
        name: "PublicProfile",
        component: () =>
          import(
            /* webpackChunkName: "PublicProfile" */ "../app/user-module/pages/PublicProfilePage"
          ),
      },
      {
        path: "change-password",
        name: "ChangePassword",
        meta: { requiresLogin: true },
        component: () =>
          import(
            /* webpackChunkName: "ChangePassword" */ "../app/user-module/pages/ChangePasswordPage"
          ),
      },
      {
        path: "courses",
        name: "Courses",
        meta: { requiresLogin: true },
        component: () =>
          import(
            /* webpackChunkName: "Courses" */ "../app/course-module/pages/CoursesPage.vue"
          ),
      },
      {
        path: "course-details/:trainingRegistrationId",
        name: "CourseDetails",
        meta: { requiresLogin: true },
        component: () =>
          import(
            /* webpackChunkName: "CourseDetails" */ "../app/course-module/pages/CourseDetailsPage"
          ),
      },
      {
        path: "course-certificate-download/:id",
        name: "CourseCertificateDownload",
        meta: { requiresLogin: true },
        component: () =>
          import(
            /* webpackChunkName: "CourseCertificateDownload" */ "../app/course-module/pages/CourseCertificatePage"
          ),
      },
      {
        path: "course-certificate/:id",
        name: "CourseCertificate",
        meta: { requiresLogin: true },
        component: () =>
          import(
            /* webpackChunkName: "CourseCertificate" */ "../app/course-module/pages/CourseCertificatePage"
          ),
      },
      {
        path: "public-course-certificate/:id",
        name: "PublicCourseCertificate",
        component: () =>
          import(
            /* webpackChunkName: "PublicCourseCertificate" */ "../app/course-module/pages/CourseCertificatePage"
          ),
      },
      {
        path: "assessments",
        name: "Assessments",
        meta: { requiresLogin: true },
        component: () =>
          import(
            /* webpackChunkName: "Assessments" */ "../app/quiz-module/pages/AssessmentsPage"
          ),
      },
      {
        path: "assessment-details/:id",
        name: "AssessmentDetails",
        meta: { requiresLogin: true },
        component: () =>
          import(
            /* webpackChunkName: "AssessmentDetails" */ "../app/quiz-module/pages/AssessmentDetailsPage"
          ),
      },
      {
        path: "assessment-attempt-details/:id",
        name: "AssessmentAttemptDetails",
        meta: { requiresLogin: true },
        component: () =>
          import(
            /* webpackChunkName: "AssessmentAttemptDetails" */ "../app/quiz-module/pages/AssessmentAttemptDetailsPage"
          ),
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  let language = getSavedLanguage() || to.params.lang;
  if (!language) language = "en";
  i18n.locale = language;

  const isAuthUser = isAuthenticatedRole(authRole());

  if (to.meta.requiresLogin === true && !isAuthUser) {
    next({ name: "SignIn", params: { lang: i18n.locale } });
  } else if (to.meta.redirectAuthUser && isAuthUser) {
    // redirect to home for authenticated users

    const user = authUser();

    if (!user.isProfileCompleted) {
      if (
        readFromLocalStorage("Skipped") &&
        readFromLocalStorage("Skipped").value
      ) {
        var parseData = new Date(readFromLocalStorage("Skipped").date);
        var today = new Date();
        var time_difference = today.getTime() - parseData.getTime();
        var days_difference = Math.trunc(
          time_difference / (1000 * 60 * 60 * 24)
        );

        if (days_difference > 2) {
          next({ name: "DetailSignUp", params: { lang: i18n.locale } });
        } else next({ name: "Home", params: { lang: i18n.locale } });
      } else next({ name: "DetailSignUp", params: { lang: i18n.locale } });
    } else next({ name: "Home", params: { lang: i18n.locale } });
  } else {
    next();
  }
});

export default router;
